// Commercial operation of InteractAds (ADR-0047): company profile, commercial terms, purchase orders,
// campaign authorizations and the revolving credit line. Pure data and validation, shared by the API,
// the database tests and the web. Money is integer micro-USD (1 USD = 1_000_000), never a float.

export const ORGANIZATION_TYPES = ["AGENCY", "DIRECT_CLIENT"] as const;
export type OrganizationType = (typeof ORGANIZATION_TYPES)[number];

/** Company review state. Separate from credit: ACTIVE never implies any credit. */
export const COMPANY_STATUSES = ["PENDING_REVIEW", "ACTIVE", "SUSPENDED", "REJECTED"] as const;
export type CompanyStatus = (typeof COMPANY_STATUSES)[number];

export const PAYMENT_MODES = ["PREPAID", "PURCHASE_ORDER", "CREDIT"] as const;
export type PaymentMode = (typeof PAYMENT_MODES)[number];

/** Net payment terms in days (0 = due on receipt). */
export const PAYMENT_TERMS_DAYS = [0, 15, 30, 45, 60] as const;

export const MICROS_PER_USD = 1_000_000;
/**
 * DECISION (owner, 2026-10-26): revolving credit, initial maximum US$10,000 per agency. Never assigned
 * automatically; a SUPER_ADMIN may approve any lower limit. Raising it needs a new explicit policy.
 * Credit covers billable CPM consumption only — never AI usage.
 */
export const CREDIT_LIMIT_MAX_MICROS = 10_000 * MICROS_PER_USD;

export const PURCHASE_ORDER_STATUSES = ["PENDING_REVIEW", "APPROVED", "REJECTED", "CANCELLED"] as const;
export type PurchaseOrderStatus = (typeof PURCHASE_ORDER_STATUSES)[number];

export const AUTHORIZATION_STATUSES = ["SUBMITTED", "APPROVED", "REJECTED", "SUPERSEDED", "CANCELLED"] as const;
export type AuthorizationStatus = (typeof AUTHORIZATION_STATUSES)[number];

/** "1234.56" → 1234560000 micro-USD; null when not a positive amount with at most 2 decimals. */
export function parseUsd(value: unknown): number | null {
  const text = typeof value === "number" ? String(value) : typeof value === "string" ? value.trim() : "";
  const m = /^(\d{1,12})(?:\.(\d{1,2}))?$/.exec(text);
  if (!m) return null;
  const micros = Number(m[1]) * MICROS_PER_USD + Number((m[2] ?? "").padEnd(2, "0")) * 10_000;
  return Number.isSafeInteger(micros) && micros > 0 ? micros : null;
}

export function formatUsdMicros(micros: number): string {
  return (micros / MICROS_PER_USD).toFixed(2);
}

// ─── Tax identification ─────────────────────────────────────────────────────

/**
 * FORMAT checks of the tax id per country (never a registry verification). Only countries whose format
 * InteractAds documents are checked specifically; any other country gets the generic check and the
 * profile says "formato no verificado" (REQUIRES VERIFICATION, docs/COMMERCIAL-ADMIN-V1.md §3).
 */
export interface TaxIdRule {
  readonly label: string;
  readonly pattern: RegExp;
  readonly hint: string;
  readonly specific: boolean;
}

export const TAX_ID_RULES: Readonly<Record<string, TaxIdRule>> = {
  HN: { label: "RTN", pattern: /^\d{14}$/, hint: "14 dígitos", specific: true },
  US: { label: "EIN", pattern: /^\d{9}$/, hint: "9 dígitos", specific: true },
  MX: { label: "RFC", pattern: /^[A-ZÑ&]{3,4}\d{6}[A-Z0-9]{3}$/, hint: "12 o 13 caracteres", specific: true },
};
const GENERIC_TAX_ID: TaxIdRule = {
  label: "Identificación tributaria",
  pattern: /^[A-Z0-9]{4,20}$/,
  hint: "4 a 20 letras o números",
  specific: false,
};

export const taxIdRule = (country: string): TaxIdRule => TAX_ID_RULES[country] ?? GENERIC_TAX_ID;

/** Uppercase, without spaces, dashes, dots or slashes: the value compared for duplicates. */
export const normalizeTaxId = (value: string): string => value.toUpperCase().replace(/[\s\-./]/g, "");

export function validateTaxId(country: string, value: string): { ok: true; normalized: string } | { ok: false } {
  const normalized = normalizeTaxId(value);
  return taxIdRule(country).pattern.test(normalized) ? { ok: true, normalized } : { ok: false };
}

/**
 * ISO 3166-1 alpha-2 codes (officially assigned). CA-O3 (owner, 2026-10-27): companies from ANY country
 * register; only the tax id FORMAT is checked (specific for HN/US/MX, generic otherwise) and no foreign tax
 * system is modelled. Invoices are issued from Honduras. Names are shown with Intl.DisplayNames (es).
 */
export const ISO_COUNTRY_CODES: readonly string[] = (
  "AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ " +
  "CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR " +
  "GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO " +
  "JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR " +
  "MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO " +
  "RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV " +
  "TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW"
).split(" ");
const COUNTRY_SET = new Set(ISO_COUNTRY_CODES);

export const isCountryCode = (value: unknown): value is string => typeof value === "string" && COUNTRY_SET.has(value);

// ─── Billing profile (CA-O3) ────────────────────────────────────────────────

/** Fiscal address in the registration country + billing contact. Required to activate a company. */
export interface BillingProfileInput {
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly city: string;
  readonly region: string;
  readonly postalCode: string | null;
  readonly billingContactName: string;
  readonly billingContactEmail: string;
  readonly billingContactPhone: string;
}

const EMAIL_SHAPE = /^[^@\s]{1,64}@[^@\s]{1,255}\.[^@\s]{2,}$/;
const PHONE_SHAPE = /^\+?[0-9 ()-]{6,24}$/;
const POSTAL_SHAPE = /^[A-Za-z0-9 -]{2,12}$/;

/** Field problems of a billing profile (empty = valid). Same limits as the table checks (0031). */
export function billingProfileProblems(
  p: BillingProfileInput
): Array<{ path: keyof BillingProfileInput; problem: string }> {
  const out: Array<{ path: keyof BillingProfileInput; problem: string }> = [];
  const len = (v: string | null, min: number, max: number) =>
    v !== null && v.trim().length >= min && v.trim().length <= max;
  if (!len(p.addressLine1, 3, 200)) out.push({ path: "addressLine1", problem: "INVALID_VALUE" });
  if (p.addressLine2 !== null && p.addressLine2.trim() !== "" && !len(p.addressLine2, 1, 200))
    out.push({ path: "addressLine2", problem: "INVALID_VALUE" });
  if (!len(p.city, 2, 120)) out.push({ path: "city", problem: "INVALID_VALUE" });
  if (!len(p.region, 2, 120)) out.push({ path: "region", problem: "INVALID_VALUE" });
  if (p.postalCode !== null && p.postalCode.trim() !== "" && !POSTAL_SHAPE.test(p.postalCode.trim()))
    out.push({ path: "postalCode", problem: "INVALID_VALUE" });
  if (!len(p.billingContactName, 2, 120)) out.push({ path: "billingContactName", problem: "INVALID_VALUE" });
  if (!EMAIL_SHAPE.test(p.billingContactEmail.trim()))
    out.push({ path: "billingContactEmail", problem: "INVALID_VALUE" });
  if (!PHONE_SHAPE.test(p.billingContactPhone.trim()))
    out.push({ path: "billingContactPhone", problem: "INVALID_VALUE" });
  return out;
}

/** Trimmed values; empty optional fields become null. */
export function normalizeBillingProfile(p: BillingProfileInput): BillingProfileInput {
  const opt = (v: string | null) => (v === null || v.trim() === "" ? null : v.trim());
  return {
    addressLine1: p.addressLine1.trim(),
    addressLine2: opt(p.addressLine2),
    city: p.city.trim(),
    region: p.region.trim(),
    postalCode: opt(p.postalCode),
    billingContactName: p.billingContactName.trim(),
    billingContactEmail: p.billingContactEmail.trim().toLowerCase(),
    billingContactPhone: p.billingContactPhone.trim(),
  };
}

// ─── Notifications (CA-O2) ──────────────────────────────────────────────────

/**
 * Commercial notification events (the outbox event_type). "operators" events can be routed to the
 * recipients configured in the Administrador Master; "company" events go to the company's commercial
 * contact (and its billing contact for credit and invoice events). Texts never carry financial amounts.
 */
export const NOTIFICATION_EVENTS = {
  "company.pending_review": { label: "Nueva empresa pendiente de aprobación", audience: "operators" },
  "purchase_order.pending": { label: "Nueva orden de compra", audience: "operators" },
  "campaign.pending_authorization": { label: "Campaña pendiente de aprobación", audience: "operators" },
  "credit.warning": { label: "Crédito próximo a agotarse", audience: "both" },
  "credit.exhausted": { label: "Crédito agotado", audience: "both" },
  "campaign.financial_hold": { label: "Campaña detenida por control financiero", audience: "both" },
  "company.approved": { label: "Empresa aprobada", audience: "company" },
  "company.rejected": { label: "Empresa rechazada", audience: "company" },
  "company.suspended": { label: "Empresa suspendida", audience: "company" },
  "purchase_order.approved": { label: "Orden de compra aprobada", audience: "company" },
  "purchase_order.rejected": { label: "Orden de compra rechazada", audience: "company" },
  "campaign.approved": { label: "Campaña aprobada", audience: "company" },
  "campaign.rejected": { label: "Campaña rechazada", audience: "company" },
} as const satisfies Record<string, { label: string; audience: "operators" | "company" | "both" }>;
export type NotificationEvent = keyof typeof NOTIFICATION_EVENTS;

/** Events an operator recipient can subscribe to in the Administrador Master. */
export const OPERATOR_NOTIFICATION_EVENTS = (Object.keys(NOTIFICATION_EVENTS) as NotificationEvent[]).filter(
  (e) => NOTIFICATION_EVENTS[e].audience !== "company"
);
